import { useEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { GROUP_CHAT_SPEC, toggleOverride } from './dev-feature-toggles';

/**
 * Client read of Symphony group chat — `symphonyGroupChat`, conducted server-side (`/api/flags/group-chat`).
 *
 * FAILS CLOSED to off until the gate answers, and on any error. It only decides whether a chat that already
 * carries a group summary is DRAWN as a group; it protects nothing. A dev-tools Feature toggles override wins over
 * the conducted value, and is inert without a dev-tools grant.
 */
const GATE_TIMEOUT_MS = 1500;

let cached: Promise<boolean> | null = null;

export function groupChatEnabled(): Promise<boolean> {
  // Checked on every call, not memoized: the override can be set mid-session from the dev panel.
  const forced = toggleOverride(GROUP_CHAT_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cached) return cached;
  cached = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/group-chat`, {
        credentials: 'include',
        signal: AbortSignal.timeout(GATE_TIMEOUT_MS),
      });
      if (!res.ok) {
        cached = null;
        return false;
      }
      return ((await res.json()) as boolean) === true;
    } catch {
      cached = null;
      return false;
    }
  })();
  return cached;
}

/** The arm, for rendering. Starts false, so a private-looking row never flashes into a group and back. */
export function useGroupChatEnabled(): boolean {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let live = true;
    void groupChatEnabled().then((on) => {
      if (live) setEnabled(on);
    });
    return () => {
      live = false;
    };
  }, []);
  return enabled;
}
